import { useMemo } from "react";
import {
  ContainerGroupPick,
  ContainerGroupPicker,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  useToast,
} from "@racoon/ui";
import { Namespace, TranslationKey, useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { getEnumValue } from "@racoon/lib";
import { useBindContainerGroupsToPickupLocationGroupMutation } from "~/shared/api/pickup-location-groups/pickup-location-groups.grpc";
import { useCompatibleContainerGroups } from "~/shared/hooks/use-compatible-container-groups";
import { canTruckServeJobType } from "../model/helpers";
import { containerGroupRequiredJobTypes } from "~/shared/lib/container-group-job-types";
import { useUnpricedGroupSchedules } from "~/shared/api/container-group-schedule/use-location-schedules";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupId: bigint;
  ownerOrganizationId: bigint;
  /** The group's default truck; compatibility is checked against it */
  defaultTruckId?: bigint;
  /** Full truck list, used to resolve the default truck's type. */
  trucks: ConnectProto.Truck[];
  locations: ConnectProto.PickupLocation[];
  /** Job types already bound per container group id (stringified). A partially
   *  bound container group stays offerable for its remaining job types. */
  boundJobTypesByCgId: Map<string, string[]>;
};

/**
 * Binds container groups to a pickup location group. The picking is the shared
 * `ContainerGroupPicker`; this only decides what is still bindable and writes
 * the links.
 */
export const BindLocationsModal = ({
  open,
  onOpenChange,
  groupId,
  ownerOrganizationId,
  defaultTruckId,
  trucks,
  locations,
  boundJobTypesByCgId,
}: Props) => {
  const t = useT();
  const { toast } = useToast();
  const [bindContainerGroups] = useBindContainerGroupsToPickupLocationGroupMutation();

  const hasTruck = defaultTruckId != null && defaultTruckId !== 0n;

  const defaultTruckTypeId = useMemo(
    () =>
      trucks.find((truck) => defaultTruckId != null && truck.id === defaultTruckId)?.truckType?.id,
    [trucks, defaultTruckId],
  );

  const { containerGroups, truckCapabilities, isLoading } = useCompatibleContainerGroups({
    ownerOrganizationId,
    truckId: defaultTruckId,
    truckTypeId: defaultTruckTypeId,
    // The modal is mounted by the group detail panel whether or not it is open,
    // so without this every panel would fetch both lists just by being shown.
    enabled: open,
  });

  // Schedule rows stand in for job types where a group's contract prices none.
  const { schedulesByCgId, hintKey } = useUnpricedGroupSchedules(containerGroups, !open);

  // Job types still bindable per container group: required job types the truck
  // can handle for the group's container type, minus already bound ones.
  const jobTypesByCgId = useMemo(() => {
    const map = new Map<string, string[]>();
    containerGroups.forEach((cg) => {
      const bound = new Set(boundJobTypesByCgId.get(String(cg.id)) ?? []);
      map.set(
        String(cg.id),
        containerGroupRequiredJobTypes(cg, schedulesByCgId.get(String(cg.id))).filter(
          (jobType) =>
            !bound.has(jobType) &&
            canTruckServeJobType(truckCapabilities, cg.containerType?.id, jobType),
        ),
      );
    });
    return map;
  }, [containerGroups, boundJobTypesByCgId, truckCapabilities, schedulesByCgId]);

  // One link per selected container group × each job type picked for that group.
  const handleSubmit = async (picks: ContainerGroupPick[]) => {
    try {
      await bindContainerGroups({
        pickupLocationGroupId: groupId,
        links: picks.map(({ containerGroup, jobType }) => ({
          containerGroupId: containerGroup.id,
          jobType: getEnumValue(ConnectProto.JobTypeSchema, jobType),
        })),
      }).unwrap();
      toast({ title: t(Namespace.PickupLocation, "groups.notifications.bound") });
      onOpenChange(false);
    } catch {
      // The modal stays open so the selection is not lost.
      toast({
        variant: "destructive",
        title: t(Namespace.PickupLocation, "groups.notifications.error"),
        description: t(Namespace.PickupLocation, "groups.notifications.errorDescription"),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!gap-0 !p-0 w-[1072px] max-w-[1072px] overflow-hidden rounded-2xl">
        <DialogHeader className="min-h-16 !p-4">
          <DialogTitle className="text-xl font-bold tracking-tight text-text-primary">
            {t(Namespace.PickupLocation, "groups.bindModal.title")}
          </DialogTitle>
        </DialogHeader>

        {hintKey && (
          <p className="px-4 pb-2 text-sm text-text-secondary">
            {t(Namespace.PickupLocation, hintKey)}
          </p>
        )}
        <div className="border-t border-border-primary">
          <ContainerGroupPicker
            locations={locations}
            containerGroups={containerGroups}
            jobTypesByCgId={jobTypesByCgId}
            emptyHint={
              !hasTruck
                ? t(Namespace.PickupLocation, "groups.noTruck")
                : isLoading
                  ? t(Namespace.PickupLocation, "loading")
                  : null
            }
            onSubmit={handleSubmit}
            labels={{
              searchPlaceholder: t(Namespace.PickupLocation, "groups.bindModal.searchLocation"),
              location: t(Namespace.PickupLocation, "groups.bindModal.location"),
              containerGroups: t(Namespace.PickupLocation, "groups.bindModal.containerGroups"),
              jobTypes: t(Namespace.PickupLocation, "groups.bindModal.jobType"),
              noLocations: t(Namespace.PickupLocation, "modal.noData"),
              selectLocation: t(Namespace.PickupLocation, "groups.bindModal.selectLocation"),
              noContainerGroups: t(Namespace.PickupLocation, "groups.bindModal.noContainerGroups"),
              selectContainerGroup: t(
                Namespace.PickupLocation,
                "groups.bindModal.selectAtLeastOneGroup",
              ),
              submit: t(Namespace.PickupLocation, "groups.bindModal.add"),
              submitWithCount: (count) =>
                t(Namespace.PickupLocation, "groups.bindModal.addWithCount", { count }),
              containersCount: (count) =>
                t(Namespace.PickupLocation, "groups.bindModal.count", { count }),
              jobTypeName: (jobType) => t(Namespace.Enum, jobType as TranslationKey),
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
